import { Box, Container, Typography, Stack, IconButton, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useLanguage } from '../contexts/LanguageContext';
import Logo from './Logo';

const FooterContainer = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? '#1E293B'
    : '#1F2937',
  color: '#FFFFFF',
  padding: '80px 0 32px',
  borderTop: theme.palette.mode === 'dark'
    ? '1px solid rgba(255, 255, 255, 0.1)'
    : 'none',
}));

const FooterLink = styled(Typography)({
  color: 'rgba(255, 255, 255, 0.7)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    color: '#FFFFFF',
    transform: 'translateX(4px)',
  },
});

const SocialButton = styled(IconButton)({
  color: 'rgba(255, 255, 255, 0.7)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    color: '#FFFFFF',
    background: 'rgba(37, 99, 235, 0.2)',
    borderColor: '#2563EB',
    transform: 'translateY(-4px)',
  },
});

export default function Footer() {
  const { t } = useLanguage();

  const footerLinks = [
    { key: 'nav.company', path: '/about/company' },
    { key: 'nav.services.software', path: '/services/software-development' },
    { key: 'nav.services.digital', path: '/services/digital-transformation' },
    { key: 'nav.services.consulting', path: '/services/it-consulting' },
    { key: 'nav.services.cloud', path: '/services/cloud-solutions' },
    { key: 'products.verilobi.title', path: '/products/verilobi' },
    { key: 'products.beachbot.title', path: '/products/beachbot' },
    { key: 'nav.blog', path: '/blog' },
  ];

  const handleLinkClick = (path: string) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <FooterContainer component="footer" role="contentinfo" id="footer">
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 4,
            mb: 6,
          }}
        >
          {/* Company Info */}
          <Stack spacing={2} component="section" aria-label="Company information">
            <Box sx={{ position: 'relative', mb: 1.5, pb: 1.5, display: 'inline-block', width: 'fit-content' }}>
              <Logo variant="white" height={50} />
              <Typography
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: '0.9rem',
                  fontWeight: 400,
                  color: '#FFFFFF',
                  position: { xs: 'static', sm: 'absolute' },
                  bottom: { sm: -6 },
                  right: { sm: 0 },
                  letterSpacing: '0.5px',
                  mt: { xs: 0.5, sm: 0 },
                }}
              >
                {t('footer.slogan')}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.8, mt: 2 }}>
              {t('footer.about.desc')}
            </Typography>
            <Stack direction="row" spacing={1} role="group" aria-label="Social media links">
              <SocialButton size="small" aria-label="LinkedIn">
                <LinkedInIcon />
              </SocialButton>
              <SocialButton size="small" aria-label="Twitter">
                <TwitterIcon />
              </SocialButton>
              <SocialButton size="small" aria-label="Facebook">
                <FacebookIcon />
              </SocialButton>
              <SocialButton size="small" aria-label="Instagram">
                <InstagramIcon />
              </SocialButton>
            </Stack>
          </Stack>

          {/* Quick Links */}
          <Stack spacing={2} component="nav" aria-label="Footer navigation">
            <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mb: 1 }}>
              {t('footer.links.title')}
            </Typography>
            {footerLinks.map((link) => (
              <FooterLink
                key={link.key}
                variant="body2"
                component={Link}
                to={link.path}
                onClick={() => handleLinkClick(link.path)}
              >
                {t(link.key)}
              </FooterLink>
            ))}
          </Stack>

          {/* Contact Info - Turkey Office */}
          <Stack spacing={2} component="section" aria-label="Turkey office contact information">
            <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mb: 1 }}>
              {t('footer.office.turkey')}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <LocationOnIcon sx={{ fontSize: '1.2rem', color: 'rgba(255, 255, 255, 0.7)', mt: 0.5 }} />
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6 }}>
                {t('footer.address.turkey')}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <PhoneIcon sx={{ fontSize: '1.2rem', color: 'rgba(255, 255, 255, 0.7)' }} />
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {t('footer.phone.turkey')}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <EmailIcon sx={{ fontSize: '1.2rem', color: 'rgba(255, 255, 255, 0.7)' }} />
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {t('footer.email')}
              </Typography>
            </Stack>
          </Stack>

          {/* Contact Info - Canada Office */}
          <Stack spacing={2} component="section" aria-label="Canada office contact information">
            <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mb: 1 }}>
              {t('footer.office.canada')}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <LocationOnIcon sx={{ fontSize: '1.2rem', color: 'rgba(255, 255, 255, 0.7)', mt: 0.5 }} />
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6 }}>
                {t('footer.address.canada')}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <PhoneIcon sx={{ fontSize: '1.2rem', color: 'rgba(255, 255, 255, 0.7)' }} />
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {t('footer.phone.canada')}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <EmailIcon sx={{ fontSize: '1.2rem', color: 'rgba(255, 255, 255, 0.7)' }} />
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {t('footer.email')}
              </Typography>
            </Stack>
          </Stack>

        </Box>

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 4 }} />

        <Typography
          variant="body2"
          textAlign="center"
          sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
        >
          {t('footer.rights')}
        </Typography>
      </Container>
    </FooterContainer>
  );
}